import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { setTaskRefresh } from '../../persisted/global/globalActions';
import { useEnabledQuery } from '../../ui/hooks/useEnabledQuery';
import { parseRawData, useOnUnauthorized } from '../resource-base/adapters/AdapterUtils';
import { get, post } from '../resource-base/APIVersion1';
import { DismissTaskMutateObject, DismissTaskMutateObjectSchema } from '../resource-base/mutateObjects';
import { useMutationWithDelay } from '../resource-base/useMutationWithDelay';
import {
    BackgroundTaskLogEntryResource,
    BackgroundTaskLogEntryResourceSchema,
    BackgroundTaskResource,
    BackgroundTaskResourceSchema,
} from './backgroundTaskResource';

export function useBackgroundTaskServiceActions() {
    const onUnauthorized = useOnUnauthorized();
    const dispatch = useDispatch();
    const getEnabled = useEnabledQuery();

    const getAllBackgroundTasksOfUser = (userId: string): Promise<BackgroundTaskResource[]> => {
        return get(`background-task?user-id=${userId}`).then((result) => {
            if (result.status === 200) {
                return result.json().then((rawData) => parseRawData(rawData, BackgroundTaskResourceSchema.array()));
            } else if (result.status === 401) {
                return onUnauthorized(result, []);
            }
            return Promise.reject('Could not get tasks!');
        });
    };

    const getTaskLogs = (taskId: string): Promise<BackgroundTaskLogEntryResource[]> => {
        return get(`background-task/${taskId}/logs`).then((result) => {
            if (result.status === 200) {
                return result
                    .json()
                    .then((rawData) => parseRawData(rawData, BackgroundTaskLogEntryResourceSchema.array()));
            } else if (result.status === 401) {
                return onUnauthorized(result, []);
            }
            return Promise.reject('Could not get task logs!');
        });
    };

    const dismissTask = (taskId: string): Promise<BackgroundTaskResource | null> => {
        return post(`background-task/${taskId}/dismiss`).then((result) => {
            if (result.status === 200) {
                return result.json().then((rawData) => parseRawData(rawData, BackgroundTaskResourceSchema));
            } else if (result.status === 401) {
                return onUnauthorized(result, null);
            }
            return Promise.reject('Could not dismiss task!');
        });
    };

    const stopTask = (taskId: string): Promise<BackgroundTaskResource | null> => {
        return post(`background-task/${taskId}/stop`).then((result) => {
            if (result.status === 200) {
                return result.json().then((rawData) => parseRawData(rawData, BackgroundTaskResourceSchema));
            } else if (result.status === 401) {
                return onUnauthorized(result, null);
            }
            return Promise.reject('Could not stop task!');
        });
    };

    const useGetAllBackgroundTasksOfUser = (userId: string) => {
        return useQuery({
            queryKey: ['BACKGROUND_TASKS'],
            queryFn: () => getAllBackgroundTasksOfUser(userId),
            refetchInterval: 5000,
            staleTime: 0,
            enabled: getEnabled(userId),
        });
    };

    // TODO use or remove
    const useGetTaskLogs = (taskId: string) => {
        return useQuery({ queryKey: ['TASK_LOGS', taskId], queryFn: () => getTaskLogs(taskId) });
    };

    const useDismissTask = () => {
        return useMutationWithDelay(
            (mutateObject: DismissTaskMutateObject) => dismissTask(mutateObject.id),
            DismissTaskMutateObjectSchema,
            {
                onSettled: () => {
                    dispatch(setTaskRefresh(true));
                },
            },
        );
    };

    const useStopTask = () => {
        return useMutationWithDelay(
            (mutateObject: DismissTaskMutateObject) => stopTask(mutateObject.id),
            DismissTaskMutateObjectSchema,
            {
                onSettled: () => {
                    dispatch(setTaskRefresh(true));
                },
            },
        );
    };

    return {
        useGetAllBackgroundTasksOfUser,
        useGetTaskLogs,
        useDismissTask,
        useStopTask,
    };
}
